import type { AnchorHTMLAttributes } from "react";
import { Link, useInRouterContext } from "react-router-dom";
import { cn } from "~/lib/utils";

const LINK_CLASSNAME = "font-medium text-primary underline underline-offset-4";

// The nextgen routes an agent is handed links to. Mirrors `_KNOWN_PATH` in
// `conversations/api/knowledge_base_parser.py`.
const IN_APP_ROUTE =
  /^\/nextgen\/((projects|datasets|agents|kb)(\/[A-Za-z0-9_-]+)?|projects\/[A-Za-z0-9_-]+\/c\/[A-Za-z0-9_-]+)\/?$/;

const EXTERNAL = /^https?:\/\//;

/**
 * Restores the leading slash on a `/nextgen` path that lost it. Other relative
 * paths have no root to resolve against, so they stay unrecognised.
 */
function rootedPath(href: string) {
  const bare = href.replace(/^\.\//, "");
  return bare.startsWith("nextgen/") ? `/${bare}` : href;
}

/**
 * Renders a link inside agent-written Markdown.
 *
 * A path outside the known routes was invented, so it renders as plain text
 * rather than a link to a 404. In-app paths route, keeping the panel open.
 */
export function AgentMarkdownLink({
  href,
  className,
  children,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement>) {
  const inRouter = useInRouterContext();
  const path = href ? rootedPath(href) : "";

  if (path && IN_APP_ROUTE.test(path)) {
    return inRouter ? (
      <Link to={path} className={cn(LINK_CLASSNAME, className)} {...props}>
        {children}
      </Link>
    ) : (
      <a href={path} className={cn(LINK_CLASSNAME, className)} {...props}>
        {children}
      </a>
    );
  }

  if (href && EXTERNAL.test(href)) {
    return (
      <a
        href={href}
        className={cn(LINK_CLASSNAME, className)}
        target="_blank"
        rel="noreferrer"
        {...props}
      >
        {children}
      </a>
    );
  }

  return <>{children}</>;
}
